/* global GM_STATE */
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { fetchData, postData } from 'helpers/fetch'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { hasOwnProperties } from 'helpers/utils'
import { getCustomBadgeClass } from 'libs/shopify'

const selectors = {
  CART_COUNT_SELECTOR: '#CartCount',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content'
}

const customBadgeTags = GM_STATE?.customs?.badge.tags || []
const customBadgeItems = GM_STATE?.customs?.badge.items || []

const cartError = async (response) => {
  const error = await response.json()

  globalEmitter.emit(emitterProps.notice.global, {
    type: 'error',
    content: error.description
  })
  throw new Error(error.description)
}

const getNativeCart = async () => {
  return fetchData('/cart.js', cartError).then((cart) => cart)
}

const getCart = async () => {
  return fetchData('/cart?view=ajax', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.count, cart.count)
    }

    return cart
  })
}

const getLineItem = (id, properties, quantity) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity
      }
    ]
  }

  return data
}

const addCartItem = async (id, properties = {}, quantity = 1) => {
  const lineItem = getLineItem(id, properties, quantity)

  return postData('/cart/add.js', lineItem, 'json', cartError).then((item) => {
    return item
  })
}

const addMultipleCartItems = async (items) => {
  return postData('/cart/add.js', items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  })
}

const updateMultipleCartItems = async (items) => {
  return postData('/cart/update.js', items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  }).catch((error) => cartError(error))
}

const updateCartItem = async (id, quantity, properties) => {
  return postData('/cart/change.js', {
    id,
    quantity,
    properties
  }, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.change, cart?.item_count)

    return cart
  })
}

const changeQuantity = async (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    return cart
  })
}

const removeCartItem = async (id) => {
  return postData('/cart/change.js', {
    id,
    quantity: 0
  }, 'json', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    }

    return cart
  })
}

const clearCart = async () => {
  return postData('/cart/clear.js', {}, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.clear, cart)

    return cart
  })
}

const reRenderCartCount = (count) => {
  const els = selectAll(selectors.CART_COUNT_SELECTOR, document.body)

  if (els) {
    if (count === 0) {
      els.forEach((el) => addClass('hidden', el))
    } else {
      els.forEach((el) => {
        el.innerText = count
        removeClass('hidden', el)
        addClass('flex', el)
      })
    }
  }
}

const removeOrphanedCharms = async (cart) => {
  if (!cart?.items?.length) return cart

  // Get parent products (non-charm items) with their SKU and timestamp
  const parentProducts = cart.items
    .filter((item) => item.properties?._isPersonaliseCharm !== 'true')
    .map((item) => ({
      sku: item.sku,
      timestamp: item.properties?._timestamp
    }))

  // Find orphaned charms (charms whose parent SKU + timestamp combo is no longer in cart)
  const orphanedCharms = cart.items.filter((item) => {
    if (item.properties?._isPersonaliseCharm !== 'true') return false

    const charmParentSku = item.properties?._parentProductSku
    const charmTimestamp = item.properties?._timestamp

    // Check if there's a matching parent product by SKU and timestamp
    return !parentProducts.some((parent) => {
      if (parent.sku !== charmParentSku) return false

      // If charm has timestamp, parent must have matching timestamp
      if (charmTimestamp) {
        return parent.timestamp === charmTimestamp
      }

      // If charm has no timestamp (legacy), match parent without timestamp
      return !parent.timestamp
    })
  })

  if (orphanedCharms.length === 0) return cart

  // Remove orphaned charms
  const updates = {}

  orphanedCharms.forEach((charm) => {
    updates[charm.key] = 0
  })

  const updatedCart = await updateMultipleCartItems({ updates })

  return updatedCart
}

const updateCart = async () => {
  let newCart = await getCart()

  if (newCart) {
    // Clean up orphaned charms before returning (wrapped in try-catch to prevent breaking cart updates)
    try {
      newCart = await removeOrphanedCharms(newCart)
    } catch (error) {
      console.warn('Failed to remove orphaned charms:', error)
    }

    reRenderCartCount(newCart.item_count)
  }

  return newCart
}

const forceReloadItem = async (item) => {
  const currentQty = item.quantity
  const nextQty = item.quantity + 1

  await changeQuantity(item.key, nextQty)

  const cart = await changeQuantity(item.key, currentQty)

  return cart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)

  if (el) {
    el.show()

    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)

    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const openCartNotice = () => {
  const el = select('cart-notice-slideout modal-dialog', document.body)

  if (el) {
    el.show()

    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)

    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartBadges = (tags, properties) => {
  const items = []

  if (!tags.length && !properties) {
    return items
  }

  if (tags.length) {
    for (const tag of tags) {
      const index = customBadgeTags.findIndex((i) => tag === i)

      if (index !== -1 && !customBadgeItems[index].hideOnCart) {
        items.push(customBadgeItems[index])
      } else {
        if (tag.includes('label-')) {
          const tagName = tag.replace('label-', '')

          items.push({
            text: tagName,
            tag,
            customClass: getCustomBadgeClass(tagName),
            backgroundColor: '',
            textColor: ''
          })
        }
      }
    }
  }

  return items.filter((item) => item)
}

const getCartProperties = (items) => {
  const arrs = []

  if (items && Object.keys(items).length > 0) {
    for (const [key, value] of Object.entries(items)) {
      arrs.push({
        name: key,
        key: value
      })
    }
  }

  return arrs
}

const getDisableChangeQuantity = (properties) => {
  return properties && properties?._disable_quantity === 'true'
}

const getItemsByKey = (items, item, key) => items.filter((cartItem) => cartItem.properties && hasOwnProperties(key, cartItem.properties) && cartItem.properties.timestamp === item.properties.timestamp)

const getMaxQuantity = (cartItem) => {
  return cartItem.inventory_management === 'shopify' && cartItem.inventory_policy === 'deny' && cartItem.inventory_quantity > 0
    ? cartItem.inventory_quantity
    : 1000
}

const removeBundlesItem = async (item) => {
  const newCart = await updateCart()
  const bundleItems = getItemsByKey(newCart.items, item, 'timestamp')

  if (bundleItems.length === 0) {
    return
  }

  const newUpdates = {}

  for (const itemCart of bundleItems) {
    newUpdates[itemCart.id] = 0
  }

  const body = {
    updates: newUpdates
  }

  await updateMultipleCartItems(body)
}

const getMaxQuantityByVariant = (cartItems, variant) => {
  if (variant.inventory_policy === 'continue') return 9999

  const locations = variant?.locations
  let quantity = variant.inventory_quantity

  if (locations?.length > 0) {
    locations.forEach(location => {
      if (location.name === 'HEAD OFFICE') quantity = location.inventory
    })
  }

  if (!cartItems.length) return quantity

  /* eslint eqeqeq: 0 */
  for (const item of cartItems) {
    if (item.id == variant.id) quantity = quantity - item.quantity
  }

  return quantity
}

/**
 * Consolidates duplicate line items that were split by Shopify due to discount allocations.
 * Items with the same variant_id and _timestamp should be treated as one logical product.
 * For charms, also checks _charmPosition to keep different positions separate.
 * For gift wrap items, checks referenceID to keep different gift wraps separate.
 */
function consolidateDuplicateItems(items) {
  const consolidated = []
  const processedKeys = new Set()

  for (const item of items) {
    if (processedKeys.has(item.key)) continue

    const timestamp = item.properties?._timestamp
    const variantId = item.variant_id
    const charmPosition = item.properties?._charmPosition
    const referenceID = item.properties?.referenceID

    // Find all items with same variant_id, timestamp, AND charm position (duplicates created by discount splitting)
    const duplicates = items.filter((other) => {
      if (processedKeys.has(other.key)) return false
      if (other.variant_id !== variantId) return false

      const otherTimestamp = other.properties?._timestamp
      const otherCharmPosition = other.properties?._charmPosition
      const otherReferenceID = other.properties?.referenceID

      // Both must have same timestamp (or both have no timestamp)
      if (timestamp !== otherTimestamp) return false

      // Must also have same charm position (so charm 1 and charm 2 stay separate even if same variant)
      if (charmPosition !== otherCharmPosition) return false

      // Don't consolidate gift wrap items with different referenceIDs (different parent products)
      if (referenceID && otherReferenceID && referenceID !== otherReferenceID) return false

      return true
    })

    if (duplicates.length > 1) {
      // Merge duplicates: use first item as base, combine quantities and track all keys
      const merged = { ...duplicates[0] }

      merged.quantity = duplicates.reduce((sum, d) => sum + d.quantity, 0)
      merged._allKeys = duplicates.map((d) => d.key) // Track all keys for updates
      merged.line_price = duplicates.reduce((sum, d) => sum + d.line_price, 0)
      merged.original_line_price = duplicates.reduce((sum, d) => sum + d.original_line_price, 0)
      merged.final_line_price = duplicates.reduce((sum, d) => sum + d.final_line_price, 0)
      merged.total_discount = duplicates.reduce((sum, d) => sum + d.total_discount, 0)

      // Merge discounts arrays
      const allDiscounts = duplicates.flatMap((d) => d.discounts || [])

      merged.discounts = allDiscounts.filter((d) => d.amount > 0)

      // Merge line_level_discount_allocations
      const allAllocations = duplicates.flatMap((d) => d.line_level_discount_allocations || [])

      merged.line_level_discount_allocations = allAllocations.filter((a) => a.amount > 0)
      merged.line_level_total_discount = duplicates.reduce((sum, d) => sum + (d.line_level_total_discount || 0), 0)

      duplicates.forEach((d) => processedKeys.add(d.key))
      consolidated.push(merged)
    } else {
      processedKeys.add(item.key)
      consolidated.push(item)
    }
  }

  return consolidated
}

function groupProducts(items) {
  // First consolidate any duplicate items split by Shopify due to discount allocations
  const consolidatedItems = consolidateDuplicateItems(items)

  return consolidatedItems
    .map((mainProduct) => {
      const { referenceID, _timestamp } = mainProduct.properties || {}
      const cloneObject = { ...mainProduct }
      const productSku = mainProduct.sku

      if (referenceID) {
        cloneObject.gift_wrap_product = consolidatedItems.find((item) => item.properties?.referenceID === referenceID && item.id !== mainProduct.id) || null
      }

      // Find engraving product by timestamp (if exists)
      if (_timestamp) {
        cloneObject.engraving_product = consolidatedItems.find((item) =>
          item.properties?._timestamp === _timestamp &&
          item.properties?._isProductEngraving &&
          item.id !== mainProduct.id
        ) || null
      }

      // Find personalise charms by SKU and timestamp (timestamp ensures correct grouping when multiple of same product)
      if (productSku) {
        cloneObject.personalise_charms = consolidatedItems.filter((item) => {
          if (item.properties?._isPersonaliseCharm !== 'true') return false
          if (item.properties?._parentProductSku !== productSku) return false

          // If parent has timestamp, charms must have matching timestamp
          if (_timestamp) {
            return item.properties?._timestamp === _timestamp
          }

          // If parent has no timestamp (legacy), match charms without timestamp
          return !item.properties?._timestamp
        }) || []
      }

      return cloneObject
    })
    .filter((item) => {
      if (item === null) return false

      const props = item.properties || {}

      if (props._isProductEngraving) return false
      if (props._isPersonaliseCharm === 'true') return false
      // Filter out gift wrap LINE ITEMS (they have referenceID but NO gift options like wrapping/box)
      // Keep main products (they have referenceID AND gift options on their properties)
      if (props.referenceID && !props.wrapping) return false

      return true
    })
}

const groupCartItems = (items) => items.map(item => {
  const referenceID = item.properties?.referenceID
  const id = item.id

  if (referenceID) {
    const index = items.findIndex((i) => i.properties?.referenceID === referenceID && i.id !== id)

    if (index < 0) return item

    const itemSameRefID = items[index]
    const product = item.properties?.wrapping ? item : itemSameRefID

    product.gift_wrap_product = itemSameRefID.properties?.wrapping ? item : itemSameRefID

    items.splice(index, 1)

    return product
  } else {
    return item
  }
}).filter((item) => item != null)

/**
 * Filters out gift wrapping properties from a properties object
 * Preserves personalization, engraving, timestamp, and pre-order properties
 * @param {Object} properties - The properties object from a cart item
 * @returns {Object} - Filtered properties without gift wrapping data
 */
const filterGiftWrappingProperties = (properties) => {
  if (!properties || typeof properties !== 'object') return {}

  const filtered = {}
  const giftWrappingKeys = [
    'referenceID',
    'box',
    'gifting_sleeve',
    'card_design',
    'wrapping',
    'combo'
  ]

  for (const [key, value] of Object.entries(properties)) {
    // Skip gift wrapping option keys
    if (giftWrappingKeys.includes(key)) continue

    // Skip card message and gift receipt keys (card_message_1, card_gift_receipt_2, etc.)
    if (key.includes('card_message') || key.includes('card_gift_receipt')) continue

    // Keep everything else (personalization, engraving, timestamps, pre-order, etc.)
    filtered[key] = value
  }

  return filtered
}

export {
  getNativeCart,
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  openCartNotice,
  getCartBadges,
  getCartProperties,
  addMultipleCartItems,
  getDisableChangeQuantity,
  forceReloadItem,
  updateMultipleCartItems,
  getItemsByKey,
  getMaxQuantity,
  removeBundlesItem,
  reRenderCartCount,
  getMaxQuantityByVariant,
  groupCartItems,
  groupProducts,
  removeOrphanedCharms,
  filterGiftWrappingProperties
}
